import { useState, useRef, useEffect, useCallback } from 'react'
import * as pdfjsLib from 'pdfjs-dist'

// =======================================================================
// TILING-BASED VIEWPORT RENDERING
// =======================================================================
// Replaces the previous "render the whole page at one raster scale"
// approach (react-pdf's <Page width={...}>) with a tiled renderer built
// directly on pdfjs-dist:
//
//   - The PDF page is loaded ONCE as a PDFPageProxy (pdfjs.getDocument).
//   - The page's PAGE_BASE_WIDTH x PAGE_BASE_HEIGHT footprint is divided
//     into a fixed grid of tiles (in PDF/world coordinate space — the
//     SAME coordinate system TakeoffCanvas already uses for geometry,
//     calibration, and Snap; nothing about that system changes here).
//   - On every zoom/pan settle, we compute which tiles intersect the
//     current viewport (the visible rectangle inside the scrollable
//     container) and render ONLY those tiles at a resolution appropriate
//     to the current zoom level (capped by device pixel ratio and a
//     sane per-tile ceiling).
//   - Tiles are cached by a (tileX, tileY, resolutionBucket) key so
//     panning back to an already-rendered tile at the same zoom bucket
//     is instant — no re-render.
//   - Tiles outside the viewport, or not yet rendered at the new
//     resolution, show a fast low-resolution placeholder (either a
//     previously-cached lower-res tile scaled up via CSS, or a blank
//     tile) so panning never blocks on rendering.
//   - Zoom changes are debounced before triggering new tile renders, so
//     rapid mouse-wheel movement does not thrash pdf.js.
//
// This component only draws pixels. It receives the page's native size
// and exposes nothing that changes coordinate math — TakeoffCanvas keeps
// computing PDF-space geometry exactly as before via its own
// PAGE_BASE_WIDTH/PAGE_BASE_HEIGHT constants, which are passed in here
// unchanged as pageWidth/pageHeight.
// =======================================================================

const TILE_SIZE = 150 // in PDF/world units. Deliberately smaller than a
                       // typical "one raster per page" tile size — this
                       // is the key lever that lets each tile reach a
                       // very high per-tile resolution (bucket) before
                       // hitting the browser's canvas pixel-dimension
                       // limit, since bucket-ceiling = MAX_CANVAS_DIMENSION_PX
                       // / TILE_SIZE. At 150 units this supports full,
                       // uncapped-quality rendering up to 2000% zoom even
                       // on devicePixelRatio=3 screens (see the derivation
                       // in code comments below) — the previous 384-unit
                       // tile size was still hitting the ceiling at ~1350%
                       // on 2x-DPI screens, which is what caused the
                       // reported blur.
const RESOLUTION_BUCKET_STEP = 0.5 // group zoom into buckets so we don't
                                    // re-render tiles for every 1% zoom
// There is intentionally NO fixed "max render scale" here. Capping tile
// resolution at an arbitrary number (e.g. 6x, 8x) is exactly the bug
// this rewrite fixes — it silently upscales a lower-resolution render
// once the zoom exceeds that number, which is visually indistinguishable
// from the original whole-page CSS-scaling bug, just moved to the tile
// level. The only real ceiling that makes sense is the browser's actual
// maximum canvas dimension (a hardware/spec limit, not a quality
// tradeoff) — see MAX_CANVAS_DIMENSION_PX. Combined with the smaller
// TILE_SIZE above, this ceiling is high enough (~54x at TILE_SIZE=150)
// that it is not expected to be reached within this viewer's supported
// zoom range (up to 2000% / 20x) even on high-DPI displays.
const MAX_CANVAS_DIMENSION_PX = 8192 // conservative, well under the ~16384px
                                      // most browsers support per canvas axis
const TILE_DEBOUNCE_MS = 180
const MAX_CACHED_TILES = 240 // simple LRU-ish cap so long sessions with
                              // lots of panning/zooming don't grow memory
                              // unbounded

export default function PdfTileLayer({ fileUrl, pageIndex, pageWidth, pageHeight, zoom, viewportRect }) {
  const [pdfPage, setPdfPage] = useState(null)
  const [pdfError, setPdfError] = useState(null)
  const [renderedTiles, setRenderedTiles] = useState(new Map()) // key -> { canvas, bucket }
  const [visibleTileKeys, setVisibleTileKeys] = useState([])

  const tileDebounceRef = useRef(null)
  const cacheOrderRef = useRef([]) // tracks insertion order for simple LRU eviction
  const renderTokenRef = useRef(0) // invalidates in-flight renders when zoom/page changes fast

  // -----------------------------------------------------------------
  // Load the PDF document + page once per fileUrl/pageIndex.
  //
  // IMPORTANT: we deliberately do NOT hand the signed URL straight to
  // pdf.js (pdfjsLib.getDocument(fileUrl)). pdf.js's own URL-loading
  // path makes its own HTTP request(s) against that URL — including,
  // depending on build/config, range requests — and a signed Storage
  // URL is not guaranteed to behave the way pdf.js's internal fetcher
  // expects in every case. The symptom in the field was "Expected other
  // data range or url parameter": pdf.js's own URL transport rejecting
  // what it got back, even though the SAME url, fetched by the browser
  // directly, returns the complete, valid, correctly-typed PDF (we
  // confirmed the Storage object's bytes are a structurally valid,
  // unencrypted, single-page PDF of the expected size).
  //
  // So instead we own the HTTP request ourselves with a plain fetch(),
  // validate what came back, and hand pdf.js the raw bytes directly via
  // { data: arrayBuffer } -- a document source pdf.js has supported
  // since early versions, with no transport step left for it to get
  // wrong. ~2.7MB is small enough that loading the whole file is a
  // non-issue for this single-sheet drawing viewer.
  // -----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false
    const abortController = new AbortController()
    let loadingTask = null

    setPdfPage(null)
    setPdfError(null)
    setRenderedTiles(new Map())
    cacheOrderRef.current = []

    if (!fileUrl) return

    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href

    async function loadPdf() {
      let response
      try {
        response = await fetch(fileUrl, { signal: abortController.signal })
      } catch (err) {
        if (err.name === 'AbortError') return // unmounted mid-fetch; not an error to surface
        if (!cancelled) setPdfError('Drawing file could not be retrieved from storage.')
        return
      }

      if (!response.ok) {
        if (!cancelled) setPdfError(`Drawing file could not be retrieved from storage (HTTP ${response.status}).`)
        return
      }

      const contentType = response.headers.get('content-type') || ''
      const contentLength = response.headers.get('content-length')

      let arrayBuffer
      try {
        arrayBuffer = await response.arrayBuffer()
      } catch (err) {
        if (cancelled) return
        setPdfError('Drawing file could not be retrieved from storage.')
        return
      }

      if (cancelled) return

      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        setPdfError('Drawing file could not be loaded: storage returned an empty response.')
        return
      }

      // Validate the actual bytes rather than trusting Content-Type alone
      // -- a storage misconfiguration or proxy can return an HTML error
      // page, a redirect body, etc. with content-type left as-is or wrong.
      const headerBytes = new Uint8Array(arrayBuffer.slice(0, 5))
      const signature = String.fromCharCode(...headerBytes)
      if (signature !== '%PDF-') {
        // Never expose the signed URL itself in this diagnostic.
        console.error('PdfTileLayer: non-PDF response', {
          status: response.status,
          contentType,
          contentLength,
          signatureBytes: Array.from(headerBytes),
        })
        setPdfError('Drawing file could not be loaded: storage returned a non-PDF response.')
        return
      }

      try {
        loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
        const pdf = await loadingTask.promise
        if (cancelled) return
        const page = await pdf.getPage((pageIndex ?? 0) + 1)
        if (!cancelled) setPdfPage(page)
      } catch (err) {
        if (!cancelled) setPdfError(err?.message || 'Drawing file could not be parsed by the PDF viewer.')
      }
    }

    loadPdf()

    return () => {
      cancelled = true
      abortController.abort()
      if (loadingTask) loadingTask.destroy()
    }
  }, [fileUrl, pageIndex])

  // -----------------------------------------------------------------
  // Compute the tile grid in PDF/world coordinates. Independent of zoom
  // — this is a fixed partition of the page footprint.
  // -----------------------------------------------------------------
  const tileCols = Math.ceil(pageWidth / TILE_SIZE)
  const tileRows = Math.ceil(pageHeight / TILE_SIZE)

  function tileKey(tx, ty, bucket) { return `${tx}:${ty}:${bucket}` }

  function resolutionBucket(zoomLevel) {
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
    const stepped = Math.max(RESOLUTION_BUCKET_STEP, Math.ceil(zoomLevel / RESOLUTION_BUCKET_STEP) * RESOLUTION_BUCKET_STEP)
    const desired = stepped * dpr

    // The only real ceiling: the physical canvas pixel-dimension limit
    // most browsers enforce (a hardware/spec constraint, not a quality
    // tradeoff). At TILE_SIZE=384 world units per tile, this allows a
    // bucket up to MAX_CANVAS_DIMENSION_PX / TILE_SIZE (~21x) before we'd
    // even risk hitting it — comfortably above the 2000% zoom ceiling
    // combined with typical devicePixelRatio values (1-3x).
    const canvasLimitedBucket = MAX_CANVAS_DIMENSION_PX / TILE_SIZE

    return Math.min(desired, canvasLimitedBucket)
  }

  // -----------------------------------------------------------------
  // Determine which tiles intersect the current viewport rectangle
  // (given in PDF/world coordinates by the parent, which already knows
  // pan/zoom — TakeoffCanvas's own screenToPdf conversion is reused for
  // this, so there is exactly one coordinate transform in the whole
  // system, not two).
  // -----------------------------------------------------------------
  const computeVisibleTiles = useCallback(() => {
    if (!viewportRect) return []
    // Pad the viewport slightly so tiles just outside the edge are
    // pre-rendered before they scroll into view, avoiding a visible pop-in.
    const pad = TILE_SIZE * 0.5
    const minTx = Math.max(0, Math.floor((viewportRect.x - pad) / TILE_SIZE))
    const maxTx = Math.min(tileCols - 1, Math.floor((viewportRect.x + viewportRect.width + pad) / TILE_SIZE))
    const minTy = Math.max(0, Math.floor((viewportRect.y - pad) / TILE_SIZE))
    const maxTy = Math.min(tileRows - 1, Math.floor((viewportRect.y + viewportRect.height + pad) / TILE_SIZE))

    const keys = []
    for (let tx = minTx; tx <= maxTx; tx++) {
      for (let ty = minTy; ty <= maxTy; ty++) {
        keys.push({ tx, ty })
      }
    }
    return keys
  }, [viewportRect, tileCols, tileRows])

  // -----------------------------------------------------------------
  // Debounced tile render trigger: recompute the visible set + target
  // resolution bucket after zoom/pan settle, then render any tiles not
  // already cached at that bucket. This is what prevents mouse-wheel
  // spam from thrashing pdf.js — only the LAST event in a burst survives
  // the debounce.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!pdfPage) return
    if (tileDebounceRef.current) clearTimeout(tileDebounceRef.current)

    tileDebounceRef.current = setTimeout(() => {
      const bucket = resolutionBucket(zoom)
      const visible = computeVisibleTiles()
      setVisibleTileKeys(visible.map(({ tx, ty }) => tileKey(tx, ty, bucket)))

      const myToken = ++renderTokenRef.current

      visible.forEach(({ tx, ty }) => {
        const key = tileKey(tx, ty, bucket)
        setRenderedTiles((prev) => {
          if (prev.has(key)) return prev // already cached at this bucket
          return prev
        })
        renderTile(tx, ty, bucket, myToken)
      })
    }, TILE_DEBOUNCE_MS)

    return () => { if (tileDebounceRef.current) clearTimeout(tileDebounceRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfPage, zoom, viewportRect])

  async function renderTile(tx, ty, bucket, myToken) {
    const key = tileKey(tx, ty, bucket)
    if (renderedTiles.has(key)) return
    if (!pdfPage) return

    // Compute this tile's rectangle in PDF/world coordinates (same space
    // as pageWidth/pageHeight, which the parent derives from its own
    // fixed PAGE_BASE_WIDTH/HEIGHT constants — untouched by this file).
    const tileWorldX = tx * TILE_SIZE
    const tileWorldY = ty * TILE_SIZE
    const tileWorldW = Math.min(TILE_SIZE, pageWidth - tileWorldX)
    const tileWorldH = Math.min(TILE_SIZE, pageHeight - tileWorldY)

    // pdf.js viewport scale: pageWidth is our fixed "100% world" footprint,
    // so the base scale to go from PDF-native units to our world units is
    // pageWidth / pdfPage.view width. Multiply by the resolution bucket to
    // get the actual raster scale for this tile at this zoom level.
    //
    // Two DIFFERENT unit spaces are involved here, and each pixel
    // dimension below must use the correct one:
    //   - tileWorldX/Y/W/H are in WORLD units (0..pageWidth/pageHeight,
    //     our fixed PAGE_BASE_WIDTH/HEIGHT footprint) — bucket alone
    //     converts these to pixels at the current zoom level, since
    //     "bucket" IS the world-space raster scale.
    //   - fullViewport / pdf.js's own rendering operates in NATIVE PDF
    //     point space, which is why getViewport() needs renderScale
    //     (baseScale * bucket) to land on the same pixel grid as the
    //     world-space canvas.
    // The earlier bug used bucket alone everywhere (too low-res once
    // baseScale != 1) — that was fixed by introducing renderScale, but
    // fixing it by applying renderScale to the WORLD-space canvas size
    // and translate as well was a second, opposite bug: it double-applied
    // baseScale, shrinking the canvas and cropping most of the tile's
    // content. The canvas backing size and translate offset must stay in
    // world-space units (bucket); only the pdf.js viewport itself uses
    // renderScale.
    const nativeViewport = pdfPage.getViewport({ scale: 1 })
    const baseScale = pageWidth / nativeViewport.width
    const renderScale = baseScale * bucket

    const fullViewport = pdfPage.getViewport({ scale: renderScale })

    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(tileWorldW * bucket)
    canvas.height = Math.ceil(tileWorldH * bucket)
    const ctx = canvas.getContext('2d')

    // Offset the render so only this tile's region lands on the canvas.
    // This must be in the SAME pixel space as the canvas itself
    // (world-space * bucket), matching the canvas dimensions above.
    ctx.translate(-tileWorldX * bucket, -tileWorldY * bucket)

    try {
      await pdfPage.render({ canvasContext: ctx, viewport: fullViewport }).promise
    } catch (err) {
      // Rendering can be legitimately cancelled if a newer render token
      // superseded this one (fast zoom/pan) — only report real failures.
      if (renderTokenRef.current === myToken) {
        setPdfError(`Tile render failed: ${err.message || err}`)
      }
      return
    }

    if (renderTokenRef.current !== myToken) return // superseded, discard

    setRenderedTiles((prev) => {
      const next = new Map(prev)

      // Critical: remove any OTHER cached entry for this same (tx, ty) at
      // a different (typically lower) bucket. Without this, a low-res
      // tile from a previous zoom level and the newly-rendered high-res
      // tile for the SAME screen position both remain in the map
      // simultaneously (they have different map keys since bucket is
      // part of the key), get rendered as two overlapping absolutely-
      // positioned elements, and whichever ends up on top by DOM order
      // is what the user actually sees — often the stale low-res one.
      // This was the real cause of blur appearing even at moderate zoom
      // after tiling was introduced: it was not a resolution math error,
      // it was stale, lower-resolution tiles never being cleared and
      // sometimes rendering on top of the correctly high-resolution ones.
      for (const existingKey of Array.from(next.keys())) {
        if (existingKey === key) continue
        const existing = next.get(existingKey)
        if (existing.tx === tx && existing.ty === ty) {
          next.delete(existingKey)
          const idx = cacheOrderRef.current.indexOf(existingKey)
          if (idx !== -1) cacheOrderRef.current.splice(idx, 1)
        }
      }

      next.set(key, { canvas, tx, ty, bucket })
      cacheOrderRef.current.push(key)
      // Simple LRU-style eviction to cap memory from long sessions.
      while (cacheOrderRef.current.length > MAX_CACHED_TILES) {
        const evictKey = cacheOrderRef.current.shift()
        if (evictKey !== key) next.delete(evictKey)
      }
      return next
    })
  }

  if (pdfError) {
    return (
      <div style={{ width: pageWidth, height: pageHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#B91C1C', fontSize: 13, padding: 20, textAlign: 'center' }}>
        PDF failed to load: {pdfError}
      </div>
    )
  }

  if (!pdfPage) {
    return <div style={{ width: pageWidth, height: pageHeight, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading PDF…</div>
  }

  return (
    <div style={{ position: 'relative', width: pageWidth, height: pageHeight, background: '#fff', pointerEvents: 'none' }}>
      {Array.from(renderedTiles.entries()).map(([mapKey, { canvas, tx, ty }]) => {
        const isCurrentlyVisible = visibleTileKeys.includes(mapKey)
        return (
          <TileImg
            key={mapKey}
            canvas={canvas}
            left={tx * TILE_SIZE}
            top={ty * TILE_SIZE}
            width={Math.min(TILE_SIZE, pageWidth - tx * TILE_SIZE)}
            height={Math.min(TILE_SIZE, pageHeight - ty * TILE_SIZE)}
            faded={!isCurrentlyVisible}
          />
        )
      })}
    </div>
  )
}

// Renders one cached tile canvas as a positioned <img>-like element. Using
// an actual <canvas> reference (drawn via a ref callback) rather than
// toDataURL avoids expensive base64 encoding for every tile.
function TileImg({ canvas, left, top, width, height, faded }) {
  const containerRef = useRef(null)
  useEffect(() => {
    if (containerRef.current && canvas) {
      containerRef.current.innerHTML = ''
      containerRef.current.appendChild(canvas)
      canvas.style.width = '100%'
      canvas.style.height = '100%'
      canvas.style.display = 'block'
    }
  }, [canvas])

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', left, top, width, height, opacity: faded ? 0.85 : 1, imageRendering: 'auto' }}
    />
  )
}
